/**
 * DataPipeline.js — run the import and posting steps from the app
 * ---------------------------------------------------------------
 * These steps used to need a terminal or a hand-typed URL. They are ordinary
 * operations on the book, so they belong in the app next to everything else.
 *
 * Every step is a dry run first. The dry run and the commit call the same code
 * and return the same report, so what you read before committing is what you
 * get — and the Commit button does not appear until you have read one. That is
 * not politeness: the ledger is append-only, so a wrong posting can only be
 * reversed afterwards, never removed.
 *
 * Order matters and the page enforces it by showing it, not by locking steps —
 * a partial run is sometimes what you want. Notices before blotters, because a
 * sale has to find the lot that delivered its shares; without it the sale books
 * at zero cost and its whole proceeds turn into realized gain.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

import api from "../api/client";
import { Badge, Button, Card, LoadingSpinner, PageHeader } from "../components/ui";
import theme from "../components/theme";

const STEPS = [
  {
    action: "backfill-funding",
    title: "Backfill deal funding",
    blurb:
      "Writes the deal-level funding entry for every deal whose LP investments " +
      "are recorded but whose ledger never got the matching line. Reads what is " +
      "already in the book — nothing to upload.",
    needs: null,
  },
  {
    action: "crawl-notices",
    title: "Fetch notices from Google Drive",
    blurb:
      "Pulls conversion, ELOC settlement and warrant exercise notices from the " +
      "shared deal folders, reads their figures, and stages them. Only downloads " +
      "what is new since last time.",
    needs: "drive",
  },
  {
    action: "import-blotter",
    title: "Import a broker blotter",
    blurb:
      "Loads an ITG or Clear Street transaction export. The dry run checks " +
      "every row against the blotter: a row already there is left as it is, " +
      "and a row that matches one imported under another key is held. On " +
      "commit, every new row whose posting is clean posts to the ledger at " +
      "once; the rest wait on the review list with the decision each needs.",
    needs: "file",
  },
  {
    action: "post-notices",
    title: "Post notices to positions",
    blurb:
      "Turns checked notices into tax lots. These are the cost basis, which is " +
      "why this runs before the blotters.",
    needs: null,
  },
  {
    action: "sweep-broker-rows",
    title: "Post broker rows that need nobody",
    blurb:
      "The run that happens by itself at import, every morning and after a " +
      "notice is confirmed. Every row whose posting is clean posts; the rest " +
      "wait on the review list with the decision each needs. It never books " +
      "a sale of more shares than the book holds as a short.",
    needs: null,
  },
  {
    action: "post-blotter",
    title: "Post broker rows by hand",
    blurb:
      "Broker rows post by themselves: at import, every morning and after a " +
      "notice is confirmed. This posts by hand whatever is left, costing each " +
      "sale against the lots FIFO. Unlike the automatic run it books a sale " +
      "the lots do not cover as a short, so read the dry run first.",
    needs: null,
  },
];

// Not part of the sweep above. This is a one-off correction — run it, read it,
// commit it, and it never needs running again — so it sits below the numbered
// steps rather than inside them, where it would read as something to do every
// time the book is brought up to date.
// Mirrors securities.models.LEAD_STATUSES. The server is the authority; this is
// only here so the triage table can tell a stranded row from a real one without
// a second round trip.
const LEAD_STAGES = ["Lead", "Pipeline", "Due Diligence", "Term Sheet", "Review", "Dead"];

const CLEANUP = [
  {
    action: "purge-deal-leads",
    oneOff: true,
    title: "Clear the deal pipeline's leftover leads",
    blurb:
      "The withdrawn Deal Pipeline board entered leads and unsigned term sheets " +
      "as deal records, which is why the bank feed was offering them as somewhere " +
      "to tag a wire. Nothing creates them any more and none of them are shown " +
      "in the app, but the rows are still in the book until this clears them. A " +
      "lead that has a security, or that anything else already points at, is a " +
      "real deal filed under a pipeline stage — it is reported and left alone.",
    needs: null,
  },
];

// Report fields worth putting in front of somebody, in the order they read well.
// Anything else stays in the raw JSON below the summary.
const HEADLINE = [
  ["posted_count", "posted"],
  ["skipped_count", "held back"],
  ["total_amount", "amount"],
  ["documents", "documents"],
  ["clean", "clean"],
  ["needs_review", "need review"],
  ["resolved_securities", "matched to a security"],
  ["resolved_deals", "matched to a deal"],
  ["fetched", "fetched"],
  ["unchanged", "already had"],
  ["created", "new rows"],
  ["updated", "updated rows"],
  ["possible_duplicate_count", "held — already in the blotter"],
  ["kept_count", "kept as decided"],
  ["rows_parsed", "rows read"],
  ["realized_pnl", "realized P&L"],
  ["proceeds", "proceeds"],
  ["uncovered_shares", "uncovered shares"],
  ["matched_count", "already reconciled"],
  ["removed_count", "leads cleared"],
  ["kept_because_it_has_a_security_count", "kept — has a security"],
  ["kept_because_it_has_children_count", "kept — something points at it"],
  ["leads_remaining", "leads left"],
  ["deals_remaining", "real deals"],
];

const money = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const looksLikeMoney = (key) =>
  /amount|pnl|proceeds|cash|total/.test(key) && !/count|shares/.test(key);

const number = (value) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(value || 0));

function Headline({ report }) {
  const rows = HEADLINE.filter(([key]) => report?.[key] !== undefined && report[key] !== null);
  if (!rows.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 22, margin: "14px 0 10px" }}>
      {rows.map(([key, label]) => (
        <div key={key}>
          <div
            style={{
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: ".6px",
              color: theme.textMuted,
              fontWeight: 600,
            }}
          >
            {label}
          </div>
          <div style={{ fontFamily: theme.fontMono, fontSize: 17 }}>
            {looksLikeMoney(key) ? money(report[key]) : number(report[key])}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Things the operator should look at before committing. */
function Warnings({ report }) {
  const notes = [];
  if (report?.overs?.length) {
    notes.push(
      `${report.overs.length} deal(s) already hold more funding in the ledger than ` +
        `the LPs invested. Those are never posted automatically — they need a ` +
        `reversing entry somebody means.`
    );
  }
  if (report?.skipped?.length) {
    // Count every reason, not the first three distinct ones. A hundred notices
    // held for one missing figure is one problem to fix; three sample strings
    // read as three unrelated problems and say nothing about which is worth
    // fixing first.
    const counts = new Map();
    report.skipped.forEach((s) => {
      const rs = s.reasons?.length ? s.reasons : [s.reason];
      rs.filter(Boolean).forEach((r) => counts.set(r, (counts.get(r) || 0) + 1));
    });
    const ranked = [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([reason, n]) => `${n}× ${reason}`);
    if (ranked.length) {
      notes.push(`${report.skipped.length} held back — ${ranked.join("; ")}`);
    }
  }
  if (report?.possible_duplicate_count) {
    notes.push(
      `${report.possible_duplicate_count} row(s) match a row already in the blotter ` +
        `under a different key: same account, date, security, quantity, price and ` +
        `cash. They are held, not imported. "possible_duplicates" in the full report ` +
        `names the row each one matches.`
    );
  }
  if (report?.kept_count) {
    notes.push(
      `${report.kept_count} row(s) already in the blotter would resolve differently ` +
        `now. They are posted or already decided (resolved to a deal, or marked ` +
        `cancelled), so the import leaves them as they are. "kept_rows" in the full ` +
        `report lists them.`
    );
  }
  if (report?.crawl?.no_text_layer?.length) {
    notes.push(
      `${report.crawl.no_text_layer.length} document(s) have no text layer — ` +
        `scans that need reading by hand.`
    );
  }
  if (report?.crawl?.failed?.length) {
    notes.push(`${report.crawl.failed.length} document(s) could not be read.`);
  }
  const ingest = report?.ingest || report;
  if (ingest?.not_notices) {
    // The deal drive holds every IC memo, definitive document and Rule 144
    // letter alongside the notices. Saying how many were passed over keeps
    // "not a notice" from looking like "nothing was there".
    notes.push(
      `${ingest.not_notices} document(s) read but not staged — they are not ` +
        `notices (IC memos, definitive docs, legal letters). Their text is kept, ` +
        `so they can be reclassified without fetching them again.`
    );
  }
  if (report?.crawl?.not_reached) {
    notes.push(
      `${report.crawl.not_reached} document(s) not reached — the limit stopped ` +
        `this run. Run it again to carry on from here.`
    );
  }
  if (!notes.length) return null;
  return (
    <div
      style={{
        background: theme.warningBg,
        border: `1px solid ${theme.warningBorder}`,
        borderRadius: theme.radius,
        padding: "10px 12px",
        margin: "10px 0",
        fontSize: 12.5,
        color: theme.textSecondary,
      }}
    >
      {notes.map((note, i) => (
        <div key={i} style={{ marginBottom: i === notes.length - 1 ? 0 : 6 }}>
          {note}
        </div>
      ))}
    </div>
  );
}

function Step({ step, index, driveConfigured, onRan }) {
  const [state, setState] = useState({ busy: false, report: null, committed: false, error: "" });
  const [file, setFile] = useState(null);
  const [broker, setBroker] = useState("itg");
  const fileInput = useRef(null);

  const blocked =
    step.needs === "drive" && !driveConfigured
      ? "Google Drive is not configured on the server yet."
      : step.needs === "file" && !file
      ? "Choose a CSV first."
      : "";

  const run = useCallback(
    async (commit) => {
      setState((s) => ({ ...s, busy: true, error: "" }));
      try {
        let response;
        if (step.needs === "file") {
          const form = new FormData();
          form.append("file", file);
          form.append("broker", broker);
          if (commit) {
            form.append("commit", "true");
            form.append("confirm", step.action);
          }
          response = await api.post(`/admin/pipeline/${step.action}/`, form);
        } else {
          response = await api.post(
            `/admin/pipeline/${step.action}/`,
            commit ? { commit: true, confirm: step.action } : {}
          );
        }
        setState({
          busy: false,
          report: response.data.report,
          committed: Boolean(response.data.committed),
          error: "",
        });
        onRan?.();
      } catch (err) {
        setState((s) => ({
          ...s,
          busy: false,
          error:
            err.response?.data?.detail ||
            err.response?.data?.error ||
            err.message ||
            "Something went wrong",
        }));
      }
    },
    [step, file, broker, onRan]
  );

  return (
    <Card
      title={step.oneOff ? step.title : `${index + 1}. ${step.title}`}
      subtitle={step.blurb}
      action={
        state.committed ? (
          <Badge text="Committed" variant="success" dot />
        ) : state.report ? (
          <Badge text="Dry run — nothing written" variant="warning" dot />
        ) : null
      }
      style={{ marginBottom: theme.space.xl }}
    >
      {step.needs === "file" && (
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
          <select
            value={broker}
            onChange={(e) => setBroker(e.target.value)}
            style={{
              padding: "7px 10px",
              border: `1px solid ${theme.border}`,
              borderRadius: theme.radius,
              font: "inherit",
              background: theme.bgInput,
            }}
          >
            <option value="itg">ITG</option>
            <option value="clearstreet">Clear Street</option>
          </select>
          <input
            ref={fileInput}
            type="file"
            accept=".csv"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setState({ busy: false, report: null, committed: false, error: "" });
            }}
            style={{ fontSize: 13 }}
          />
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <Button
          onClick={() => run(false)}
          disabled={state.busy || Boolean(blocked)}
          variant="secondary"
        >
          {state.busy ? "Running…" : "Dry run"}
        </Button>

        {/* Only after a dry run has been read. The ledger cannot be un-posted. */}
        {state.report && !state.committed && (
          <Button onClick={() => run(true)} disabled={state.busy}>
            Commit this
          </Button>
        )}

        {state.committed && (
          <Button onClick={() => run(false)} variant="secondary" disabled={state.busy}>
            Re-check
          </Button>
        )}

        {blocked && (
          <span style={{ fontSize: 12, color: theme.textMuted }}>{blocked}</span>
        )}
      </div>

      {state.error && (
        <div
          style={{
            background: theme.dangerBg,
            border: `1px solid ${theme.dangerBorder}`,
            borderRadius: theme.radius,
            padding: "10px 12px",
            marginTop: 12,
            fontSize: 13,
            color: theme.danger,
          }}
        >
          {state.error}
        </div>
      )}

      {state.report && (
        <>
          <Headline report={state.report.ingest || state.report} />
          <Warnings report={state.report} />
          <details style={{ marginTop: 6 }}>
            <summary style={{ cursor: "pointer", fontSize: 12, color: theme.textMuted }}>
              Everything the run reported
            </summary>
            <pre
              style={{
                background: theme.bgSubtle,
                border: `1px solid ${theme.borderLight}`,
                borderRadius: theme.radius,
                padding: 12,
                marginTop: 8,
                fontSize: 11,
                fontFamily: theme.fontMono,
                maxHeight: 320,
                overflow: "auto",
              }}
            >
              {JSON.stringify(state.report, null, 2)}
            </pre>
          </details>
        </>
      )}
    </Card>
  );
}

function ServerStatus({ server }) {
  const [open, setOpen] = useState(false);
  if (!server) return null;
  const platform = server.platform || {};

  return (
    <Card
      title="This server"
      subtitle="What the running process actually holds — not what the dashboard shows"
      action={
        <Button size="sm" variant="secondary" onClick={() => setOpen(!open)}>
          {open ? "Hide" : "Show"}
        </Button>
      }
      style={{ marginBottom: theme.space.xl }}
    >
      {Object.keys(platform).length > 0 ? (
        <div style={{ display: "flex", gap: 26, flexWrap: "wrap", fontSize: 12.5 }}>
          {Object.entries(platform).map(([label, value]) => (
            <div key={label}>
              <div
                style={{
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: ".6px",
                  color: theme.textMuted,
                  fontWeight: 600,
                }}
              >
                {label}
              </div>
              <div style={{ fontFamily: theme.fontMono }}>
                {label === "commit" ? String(value).slice(0, 7) : value}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: theme.textMuted }}>
          This process is not reporting any platform identifiers, so it is
          probably not running on Railway.
        </div>
      )}

      {open && (
        <div style={{ marginTop: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <tbody>
              {Object.entries(server.variables || {}).map(([name, info]) => (
                <tr key={name} style={{ borderTop: `1px solid ${theme.borderLight}` }}>
                  <td style={{ padding: "7px 0", width: 26 }}>
                    <span style={{ color: info.set && !info.empty ? theme.success : theme.textDisabled }}>
                      {info.set && !info.empty ? "\u2713" : "\u2717"}
                    </span>
                  </td>
                  <td style={{ padding: "7px 0", fontFamily: theme.fontMono }}>{name}</td>
                  <td style={{ padding: "7px 0", color: theme.textMuted }}>{info.purpose}</td>
                  <td
                    style={{
                      padding: "7px 0",
                      textAlign: "right",
                      color: theme.textMuted,
                      fontFamily: theme.fontMono,
                    }}
                  >
                    {info.set ? (info.empty ? "set but empty" : `${info.length} chars`) : "not set"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <OperatorAccount account={server.operator_account} />
          <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 12, lineHeight: 1.6 }}>
            {server.note}
          </div>
        </div>
      )}
    </Card>
  );
}

// Setting ADMIN_PASSWORD and being able to sign in are different things, and
// the login endpoint says "no active account" for every reason there is.
function OperatorAccount({ account }) {
  if (!account) return null;

  let state;
  if (account.error) {
    state = { ok: false, text: `could not be read — ${account.error}` };
  } else if (!account.exists) {
    state = {
      ok: false,
      text: `no ${account.username_expected} account on this database`,
    };
  } else if (account.matches_admin_password === false) {
    state = {
      ok: false,
      text: `${account.username} exists but its password is not ADMIN_PASSWORD — the reset at boot did not run`,
    };
  } else if (!account.is_active || !account.is_superuser) {
    state = { ok: false, text: `${account.username} is not an active superuser` };
  } else if (account.matches_admin_password === true) {
    state = { ok: true, text: `${account.username} signs in with ADMIN_PASSWORD` };
  } else {
    state = { ok: true, text: `${account.username} is an active superuser` };
  }

  return (
    <div
      style={{
        marginTop: 14,
        paddingTop: 12,
        borderTop: `1px solid ${theme.borderLight}`,
        fontSize: 12.5,
        display: "flex",
        gap: 8,
      }}
    >
      <span style={{ color: state.ok ? theme.success : theme.danger }}>
        {state.ok ? "✓" : "✗"}
      </span>
      <span style={{ color: theme.textSecondary }}>Operator account: {state.text}</span>
    </div>
  );
}

function DriveStatus({ index, onTested }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const drive = index.drive || {};

  const test = async () => {
    setBusy(true);
    try {
      const response = await api.post("/admin/pipeline/test-drive/", {});
      setResult(response.data.report);
      onTested?.();
    } catch (err) {
      setResult({ problem: err.response?.data?.detail || err.message });
    }
    setBusy(false);
  };

  const shown = result || drive;
  const working = result?.reached_drive && !result?.problem;

  // Each variable on its own line. "Not configured" as a single verdict does
  // not tell you which of three things to go and fix.
  const rows = [
    ["Key file variable", shown.credentials_json_set || shown.credentials_b64_set],
    ["Key file is readable", shown.credentials_valid],
    ["Folder id variable", shown.folder_id_set],
    ...(result ? [["Google answered", Boolean(result.reached_drive)]] : []),
  ];

  return (
    <Card
      title="Google Drive"
      subtitle="Where the notices come from"
      action={
        working ? (
          <Badge text="Working" variant="success" dot />
        ) : shown.credentials_valid && shown.folder_id_set ? (
          <Badge text="Configured — untested" variant="warning" dot />
        ) : (
          <Badge text="Not configured" variant="warning" dot />
        )
      }
      style={{ marginBottom: theme.space.xl }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginBottom: 12 }}>
        {rows.map(([label, ok]) => (
          <div key={label} style={{ fontSize: 12.5 }}>
            <span style={{ color: ok ? theme.success : theme.danger, marginRight: 6 }}>
              {ok ? "\u2713" : "\u2717"}
            </span>
            {label}
          </div>
        ))}
      </div>

      {shown.service_account_email && (
        <div style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 8 }}>
          Share your Drive folder with this address, as Viewer:{" "}
          <span style={{ fontFamily: theme.fontMono }}>{shown.service_account_email}</span>
        </div>
      )}
      {shown.folder_id && (
        <div style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 8 }}>
          Folder id it will read:{" "}
          <span style={{ fontFamily: theme.fontMono }}>{shown.folder_id}</span>
          {result?.folder_name ? ` — "${result.folder_name}"` : ""}
          {result?.is_shared_drive ? " (shared drive)" : ""}
        </div>
      )}
      {result?.sample_files?.length > 0 && (
        <div style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 8 }}>
          Found, for example: {result.sample_files.slice(0, 3).join(", ")}
        </div>
      )}

      {shown.problem && (
        <div
          style={{
            background: theme.warningBg,
            border: `1px solid ${theme.warningBorder}`,
            borderRadius: theme.radius,
            padding: "10px 12px",
            margin: "8px 0",
            fontSize: 12.5,
            color: theme.textSecondary,
          }}
        >
          {shown.problem}
        </div>
      )}

      <Button onClick={test} disabled={busy} variant="secondary">
        {busy ? "Testing…" : "Test connection"}
      </Button>
    </Card>
  );
}


// Deal records with nothing in them: on one of the withdrawn Deal Pipeline
// board's stages, or carrying no security at all.
//
// This is the only place they can be reached. The deals list hides a pipeline
// stage on purpose — so no picker offers a term sheet as somewhere to tag a wire
// — and a row corrected to Closed lands on the Deals page, which is not where
// anyone wants to meet forty of them.
//
// Two populations, and the difference is the whole point of the counts in the
// subtitle:
//
//   still on a stage      the purge below clears these in bulk. Nothing to do.
//   corrected but empty   somebody pressed Active or Closed on a row that was
//                         not a deal. The purge cannot see these any more —
//                         correcting the stage is what takes a row out of its
//                         scope — so they have to be deleted here.
//
// Which is why the delete is by ticked selection and never by rule. "Closed,
// no security, nothing attached" is also the shape of Klaria Pharma AB's
// bridge: never funded, so no instrument, but Helena paid counsel and
// Guidepoint to look at it and the record has to stay for the costs to land on
// (D-077). No rule can tell those two apart. A person can.
function StrandedDeals() {
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/securities/deals/", { params: { include_leads: "true" } });
      const all = res.data.results || res.data || [];
      setRows(
        all
          .filter((d) => LEAD_STAGES.includes(d.status) || !d.security)
          .sort((a, b) => a.id - b.id)
      );
      setSelected(new Set());
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Could not load them");
      setRows([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (deal, status) => {
    setBusy(true);
    setError("");
    setNote("");
    try {
      await api.patch(`/securities/deals/${deal.id}/`, { status });
      await load();
    } catch (err) {
      const body = err.response?.data;
      setError(
        typeof body === "object" && body
          ? Object.entries(body).map(([k, v]) => `${k}: ${v}`).join("; ")
          : err.message || "Could not save"
      );
    } finally {
      setBusy(false);
    }
  };

  // Same guard the purge uses, asked of the server rather than assumed: a row
  // anything points at is a deal somebody worked on, whatever its stage says,
  // and the ledger is append-only, so orphaning entries cannot be undone. A row
  // that turns out not to be empty is skipped and named, not deleted.
  const deleteSelected = async () => {
    const targets = rows.filter((d) => selected.has(d.id));
    if (!targets.length) return;
    if (
      !window.confirm(
        `Delete ${targets.length} deal record(s)?\n\n` +
          "Any that turn out to have something attached will be skipped and named. " +
          "This cannot be undone."
      )
    ) {
      return;
    }

    setBusy(true);
    setError("");
    setNote("");
    let deleted = 0;
    const skipped = [];
    const failed = [];

    for (const deal of targets) {
      try {
        const preview = await api.get(`/securities/deals/${deal.id}/cascade/`);
        const attached = Object.entries(preview.data.will_delete || {}).filter(
          ([, count]) => typeof count === "number" && count > 0
        );
        if (attached.length) {
          skipped.push(
            `${deal.deal_name} (${attached
              .map(([t, c]) => `${c} ${t.replace(/_/g, " ")}`)
              .join(", ")})`
          );
          continue;
        }
        await api.delete(`/securities/deals/${deal.id}/cascade/`);
        deleted += 1;
      } catch (err) {
        failed.push(`${deal.deal_name}: ${err.response?.data?.error || err.message}`);
      }
    }

    await load();
    setBusy(false);
    setNote(`Deleted ${deleted}.`);
    if (skipped.length) {
      setError(
        `Kept ${skipped.length} — something already points at them, so they are real ` +
          `deals: ${skipped.join("; ")}`
      );
    } else if (failed.length) {
      setError(`${failed.length} failed: ${failed.join("; ")}`);
    }
  };

  if (rows === null) return null;

  const onStage = rows.filter((d) => LEAD_STAGES.includes(d.status));
  const corrected = rows.filter((d) => !LEAD_STAGES.includes(d.status));
  // Only rows the board created. The rest are real deals that simply have no
  // instrument attached yet — same shape, opposite treatment — so select-all
  // must not reach them. Ticking one by hand is still allowed; deciding for
  // somebody is not.
  const boardMade = corrected.filter((d) => d.from_pipeline_board);
  const ownWork = corrected.filter((d) => !d.from_pipeline_board);
  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const cell = { padding: "6px 10px", borderBottom: `1px solid ${theme.borderLight}` };
  const banner = (text, danger) => (
    <div
      style={{
        background: danger ? theme.dangerBg : theme.bgSubtle,
        border: `1px solid ${danger ? theme.dangerBorder : theme.border}`,
        borderRadius: theme.radius,
        padding: "10px 12px",
        marginBottom: 12,
        fontSize: 13,
        color: danger ? theme.danger : theme.textSecondary,
      }}
    >
      {text}
    </div>
  );

  return (
    <Card
      title="Deal records with nothing in them"
      subtitle={
        rows.length
          ? `${boardMade.length} the pipeline board created, corrected to Active or ` +
            `Closed and still empty — tick and delete these, the purge cannot see ` +
            `them any more. ` +
            (ownWork.length
              ? `${ownWork.length} real deal(s) with no security attached — not the ` +
                `board's, listed so you can see them, left unticked. `
              : "") +
            `${onStage.length} still on a pipeline stage — the purge below clears those.`
          : "None left. Every deal record names a security and reads Active or Closed."
      }
      style={{ marginBottom: theme.space.xl }}
    >
      {error && banner(error, true)}
      {note && !error && banner(note, false)}

      {rows.length === 0 ? (
        <div style={{ fontSize: 13, color: theme.textMuted }}>Nothing to triage.</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() =>
                setSelected(
                  selected.size === boardMade.length
                    ? new Set()
                    : new Set(boardMade.map((d) => d.id))
                )
              }
            >
              {selected.size === boardMade.length && boardMade.length
                ? "Clear selection"
                : `Select all ${boardMade.length} the board created`}
            </Button>
            <Button size="sm" variant="danger" disabled={busy || !selected.size} onClick={deleteSelected}>
              {busy ? "Working…" : `Delete selected (${selected.size})`}
            </Button>
            <span style={{ fontSize: 12, color: theme.textMuted }}>
              Select-all ticks only rows marked "Pipeline board". Untick anything you
              want to keep — Klaria's bridge carries real costs.
            </span>
          </div>

          <div style={{ maxHeight: 460, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["", "Deal", "Counterparty", "Stage", "Origin", ""].map((h, i) => (
                    <th
                      key={i}
                      style={{
                        textAlign: "left",
                        padding: "6px 10px",
                        borderBottom: `1px solid ${theme.border}`,
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: ".5px",
                        color: theme.textMuted,
                        position: "sticky",
                        top: 0,
                        background: theme.bgSubtle,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((deal) => {
                  const stranded = !LEAD_STAGES.includes(deal.status);
                  return (
                    <tr key={deal.id} style={{ opacity: stranded ? 1 : 0.55 }}>
                      <td style={cell}>
                        <input
                          type="checkbox"
                          checked={selected.has(deal.id)}
                          disabled={busy}
                          onChange={() => toggle(deal.id)}
                        />
                      </td>
                      <td style={cell}>{deal.deal_name}</td>
                      <td style={{ ...cell, color: theme.textSecondary }}>{deal.counterparty}</td>
                      <td style={{ ...cell, color: theme.textMuted }}>{deal.status}</td>
                      <td style={{ ...cell, color: theme.textMuted, fontSize: 12 }}>
                        {deal.from_pipeline_board ? "Pipeline board" : "Yours"}
                      </td>
                      <td style={cell}>
                        <div style={{ display: "flex", gap: 6 }}>
                          {["Active", "Closed"].map((status) => (
                            <Button
                              key={status}
                              size="sm"
                              variant="secondary"
                              disabled={busy || deal.status === status}
                              onClick={() => setStatus(deal, status)}
                            >
                              {status}
                            </Button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

export default function DataPipeline() {
  const [index, setIndex] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api
      .get("/admin/pipeline/")
      .then((res) => setIndex(res.data))
      .catch((err) =>
        setError(
          err.response?.status === 403
            ? "This page needs a superuser account."
            : err.response?.data?.detail || err.message || "Could not reach the server"
        )
      );
  }, []);

  useEffect(load, [load]);

  if (error) {
    return (
      <>
        <PageHeader title="Data Pipeline" />
        <Card>
          <div style={{ color: theme.danger, fontSize: 14 }}>{error}</div>
        </Card>
      </>
    );
  }
  if (!index) return <LoadingSpinner message="Checking what the server can do…" />;

  return (
    <>
      <PageHeader
        title="Data Pipeline"
        subtitle="Bring the book up to date. Every step is a dry run first."
      />

      <ServerStatus server={index.server} />

      <DriveStatus index={index} onTested={load} />

      {STEPS.map((step, i) => (
        <Step
          key={step.action}
          step={step}
          index={i}
          driveConfigured={index.drive_configured}
          onRan={load}
        />
      ))}

      <PageHeader
        title="One-off cleanups"
        subtitle="Run once, then never again — not part of the sweep above"
      />

      <StrandedDeals />

      {CLEANUP.map((step, i) => (
        <Step
          key={step.action}
          step={step}
          index={i}
          driveConfigured={index.drive_configured}
          onRan={load}
        />
      ))}

      <Card title="If something goes in wrong" subtitle="Corrections, not deletions">
        <div style={{ fontSize: 13, color: theme.textSecondary, lineHeight: 1.65 }}>
          The ledger is append-only — the database refuses to update or delete an
          entry — so a mistake is corrected by posting its mirror. The original
          stays visible, the pair nets to nothing, and the correction is on the
          record rather than hidden. Every reversal carries the reason you give
          it, which is what makes it readable months later in a trial balance.
          <div style={{ marginTop: 10, fontFamily: theme.fontMono, fontSize: 12 }}>
            POST /api/v1/admin/pipeline/reverse-transaction/
            {"  { transaction_id, reason }"}
          </div>
        </div>
      </Card>
    </>
  );
}
